import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, PlayCircle, Wrench } from "lucide-react";
import { scrollToId, track } from "../../lib/api";

const LINES = ["Votre stock", "vous prévient", "avant de manquer."];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section id="hero" ref={ref} className="relative overflow-hidden pt-28 sm:pt-36 pb-16 grain" data-testid="hero-section">
      <div className="spotlight absolute inset-0" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
        <motion.div style={{ opacity: fade }}>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 border border-[#FFC700]/50 rounded-full px-4 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[#FFC700] animate-pulse" />
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#FFC700]">Pré-lancement — France</span>
          </motion.div>

          <h1 className="font-display font-extrabold uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-[#F9FAFB]" data-testid="hero-title">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "text-[#FFC700]" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 text-base sm:text-lg text-gray-400 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            STOCKBOX 8 est la boîte connectée pensée pour les professionnels qui veulent connaître
            leur stock de petites pièces en un coup d'œil.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <button
              onClick={() => { track("click_cta_hero", { position: "hero" }); scrollToId("questionnaire"); }}
              data-testid="hero-primary-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#FFC700] text-[#0C0D0E] font-bold text-sm sm:text-base px-8 py-4 rounded-full uppercase tracking-wide hover:bg-[#FFD83D] hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Je veux être informé du lancement <ArrowRight size={18} />
            </button>
            <button
              onClick={() => { track("click_cta_secondary", { position: "hero" }); scrollToId("fonctionnement"); }}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center justify-center gap-2 border border-[#2E333D] text-gray-200 font-semibold text-sm sm:text-base px-8 py-4 rounded-full uppercase tracking-wide hover:border-[#FFC700] hover:text-[#FFC700] transition-colors"
            >
              <PlayCircle size={18} /> Découvrir comment ça marche
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-500"
            data-testid="hero-dev-notice"
          >
            <Wrench size={13} className="text-[#FFC700]" /> Produit actuellement en développement
          </motion.p>
        </motion.div>

        <motion.div style={{ y: imgY }} className="relative" data-testid="hero-product">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-[#FFC700]/10 blur-3xl rounded-full" />
            <div className="relative border border-[#2E333D] rounded-2xl overflow-hidden bg-[#141619] shadow-2xl shadow-black/60">
              <img
                src="/images/product-closed.jpg"
                alt="Concept STOCKBOX 8 — boîte de rangement professionnelle noire, couvercle transparent, loquets jaunes, 8 compartiments"
                className="w-full h-auto object-cover"
                loading="eager"
                data-testid="hero-product-image"
              />
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#0C0D0E] to-transparent p-5 flex items-end justify-between">
                <span className="font-mono text-[10px] uppercase tracking-widest text-gray-400">Visuel concept — non contractuel</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFC700]">≈ 45 × 35 × 12 cm</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
